import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  VStack,
  Image,
  Box,
  Icon,
  useColorModeValue,
  Divider,
} from '@chakra-ui/react';
import { FiRefreshCw } from 'react-icons/fi';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  isSpotifyAuthenticated: boolean;
  isAppleAuthenticated: boolean;
  spotifyLogin: () => void;
  appleLogin: () => void;
  isAuthenticating: boolean;
  authError: string | null;
  isAppleMusicInitializing: boolean;
}

const AuthModal: React.FC<AuthModalProps> = ({
  isOpen,
  onClose,
  isSpotifyAuthenticated,
  isAppleAuthenticated,
  spotifyLogin,
  appleLogin,
  isAuthenticating,
  authError,
  isAppleMusicInitializing,
}) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay backdropFilter="blur(8px)" />
      <ModalContent bg={bgColor} borderColor={borderColor} borderWidth="1px">
        <ModalHeader textAlign="center">Connect Music Service</ModalHeader>
        <ModalCloseButton />
        
        <ModalBody pb={6}>
          <VStack spacing={6} align="center">
            <Text textAlign="center">
              Connect your music streaming service to see synchronized lyrics for your favorite songs.
            </Text>
            
            <Divider />
            
            <VStack spacing={4} width="100%">
              <Button
                size="lg"
                width="full"
                colorScheme="green"
                leftIcon={
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C5.4 0 0 5.4 0 12C0 18.6 5.4 24 12 24C18.6 24 24 18.6 24 12C24 5.4 18.66 0 12 0ZM17.521 17.34C17.281 17.699 16.861 17.819 16.5 17.58C13.68 15.801 10.14 15.42 5.939 16.38C5.521 16.5 5.1 16.26 4.98 15.84C4.86 15.42 5.1 15 5.52 14.88C10.14 13.8 14.1 14.25 17.28 16.26C17.639 16.5 17.76 16.98 17.52 17.34H17.521ZM18.961 14.04C18.66 14.46 18.12 14.64 17.7 14.34C14.46 12.36 9.72 11.76 5.82 12.9C5.34 13.02 4.86 12.78 4.74 12.3C4.62 11.82 4.86 11.34 5.34 11.22C9.9 9.96 15.12 10.62 18.84 12.9C19.201 13.14 19.38 13.74 18.96 14.04H18.961ZM19.08 10.68C15.18 8.4 8.82 8.16 5.16 9.42C4.56 9.6 3.96 9.24 3.78 8.64C3.6 8.04 3.96 7.44 4.56 7.26C8.82 5.76 15.9 6 20.34 8.64C20.88 8.94 21.06 9.66 20.76 10.14C20.46 10.62 19.74 10.86 19.26 10.56C19.2 10.62 19.14 10.68 19.08 10.68Z" fill="currentColor"/>
                  </svg>
                }
                onClick={spotifyLogin}
                isLoading={isAuthenticating && !isSpotifyAuthenticated}
                isDisabled={isSpotifyAuthenticated}
              >
                {isSpotifyAuthenticated ? 'Connected to Spotify' : 'Connect with Spotify'}
              </Button>
              
              <Button
                size="lg"
                width="full"
                colorScheme="pink"
                leftIcon={
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.9947 18.19C23.4707 19.411 22.9947 20.421 22.3547 21.579C21.5227 23.133 20.8387 24 19.5787 24C18.7387 24 18.0307 23.667 17.1987 23.332C16.3347 22.974 15.4227 22.604 14.1547 22.604C12.8107 22.604 11.8747 22.974 10.9987 23.334C10.1787 23.667 9.43475 24 8.67475 24C7.32475 24 6.59875 23.057 5.69075 21.471C4.58675 19.579 3.67475 16.911 3.67475 14.357C3.67475 12.151 4.34675 10.269 5.42275 8.996C6.30575 7.94 7.63275 7.241 9.01275 7.241C9.91675 7.241 10.6987 7.581 11.3907 7.883C12.0347 8.168 12.6027 8.423 13.1867 8.423C13.6707 8.423 14.2227 8.168 14.8547 7.886C15.5947 7.56 16.4547 7.184 17.4947 7.218C18.5427 7.25 19.9467 7.604 20.9787 8.76C19.5467 9.781 18.7467 11.24 18.7467 12.766C18.7467 14.55 19.5867 16.045 21.0867 16.971C21.7627 17.409 22.4947 17.697 23.3107 17.83C23.5467 17.947 23.7787 18.066 23.9947 18.19ZM17.3547 0C17.4547 2.103 16.5147 4.142 15.1387 5.531C13.7547 6.92 11.8667 7.629 10.1387 7.47C9.98675 5.46 10.9987 3.357 12.2947 2.069C13.7227 0.643 15.6827 0 17.3547 0Z" fill="currentColor"/>
                  </svg>
                }
                onClick={appleLogin}
                isLoading={(isAuthenticating && !isAppleAuthenticated) || isAppleMusicInitializing}
                loadingText={isAppleMusicInitializing ? "Initializing..." : "Connecting..."}
                isDisabled={isAppleAuthenticated}
              >
                {isAppleAuthenticated ? 'Connected to Apple Music' : 'Connect with Apple Music'}
              </Button>
              
              {authError && (
                <Text color="red.500" fontSize="sm">
                  {authError}
                </Text>
              )}
            </VStack>
          </VStack>
        </ModalBody>
        
        <ModalFooter>
          <Button onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AuthModal; 